import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  AutocompleteItem,
  Button,
  Modal,
  ModalContent,
} from '@nextui-org/react';
import {
  getShipmentCaseOptions,
  type CaseCommandResult,
  type CaseOptions,
  type CaseScope,
} from 'common/src/api/shipment-cases';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { Alert, LoadingState } from '../../components/ui';
import { useScopedRequest } from '../shipments/arrival-manager/use-scoped-request';
import NewCaseForm, { NewCaseSection } from './NewCaseForm';

export default function NewCaseModal({
  scope,
  identity,
  initialShipment,
  onClose,
  onSaved,
}: {
  scope: CaseScope;
  identity: string;
  initialShipment: number | null;
  onClose: () => void;
  onSaved: (r: CaseCommandResult) => void;
}) {
  const { t } = useTranslation('shipmentCases');
  type ShipmentOption = CaseOptions['shipments'][number];
  const [picker, setPicker] = useState<{
    id: number | null;
    text: string;
    option: ShipmentOption | null;
  }>({ id: initialShipment, text: '', option: null });
  const [query, setQuery] = useState('');
  const [locked, setLocked] = useState(false);
  // Último despacho elegido: el formulario solo se reinicia al cambiarlo, no al
  // escribir en el buscador.
  const [formShipment, setFormShipment] = useState(initialShipment);
  useEffect(() => {
    if (picker.id) setFormShipment(picker.id);
  }, [picker.id]);
  useEffect(() => {
    const timer = setTimeout(() => setQuery(picker.text), 250);
    return () => clearTimeout(timer);
  }, [picker.text]);
  const loadSearch = useCallback(
    (signal: AbortSignal) => getShipmentCaseOptions(scope, query, null, signal),
    [scope, query]
  );
  const search = useScopedRequest(`${identity}:search:${query}`, loadSearch);
  const loadCargo = useCallback(
    (signal: AbortSignal) =>
      getShipmentCaseOptions(scope, '', picker.id, signal),
    [scope, picker.id]
  );
  const cargo = useScopedRequest(
    picker.id ? `${identity}:cargo:${picker.id}` : null,
    loadCargo
  );
  const selected =
    picker.option || cargo.data?.shipments.find((s) => s.id === picker.id);
  const waitingSearch = picker.text !== query;
  const items = useMemo(() => {
    const rows = new Map(
      (waitingSearch ? [] : search.data?.shipments || []).map((s) => [s.id, s])
    );
    if (selected) rows.set(selected.id, selected);
    return [...rows.values()];
  }, [waitingSearch, search.data, selected]);
  const error = search.error || cargo.error;
  const ready = Boolean(picker.id && cargo.data);
  const weighted = (cargo.data?.cargo || []).filter((b) => b.kilos > 0);
  const totalKilos = weighted.reduce((n, b) => n + Number(b.kilos), 0);

  const despacho = (
    <NewCaseSection
      title={t('Despacho')}
      help={t('Escribe para buscar por referencia, cliente o contenedor.')}
      last={!ready}
    >
      <Autocomplete
        label={t('Buscar despacho')}
        placeholder={t('Referencia, contenedor o cliente')}
        items={items}
        selectedKey={picker.id ? String(picker.id) : null}
        inputValue={
          picker.text || (picker.id ? selected?.reference_number || '' : '')
        }
        isDisabled={locked}
        isLoading={waitingSearch || search.loading || cargo.loading}
        onInputChange={(text) => {
          if (locked) return;
          setPicker((previous) => {
            const current =
              previous.option ||
              (selected?.id === previous.id ? selected : null);
            return current && text === current.reference_number
              ? { ...previous, text }
              : { id: null, text, option: null };
          });
        }}
        onSelectionChange={(key) => {
          if (locked) return;
          const option = items.find((s) => String(s.id) === key);
          setPicker((previous) =>
            option
              ? { id: option.id, text: option.reference_number, option }
              : { id: null, text: previous.text, option: null }
          );
        }}
        listboxProps={{
          emptyContent: t(
            waitingSearch || search.loading
              ? 'Buscando despachos…'
              : 'No hay despachos con esa búsqueda.'
          ),
        }}
      >
        {(s) => (
          <AutocompleteItem
            key={String(s.id)}
            textValue={s.reference_number}
            description={`${s.container_number || '—'} · ${s.receiver || '—'}`}
          >
            {s.reference_number}
          </AutocompleteItem>
        )}
      </Autocomplete>
      {error && (
        <Alert variant='danger' showIcon>
          {error}
          <Button
            variant='flat'
            onPress={() => {
              if (search.error) void search.reload();
              if (cargo.error) void cargo.reload();
            }}
          >
            {t('Reintentar')}
          </Button>
        </Alert>
      )}
      {picker.id && cargo.loading && !cargo.data && (
        <LoadingState label={t('Cargando carga del despacho…')} />
      )}
      {ready && selected && (
        <div className='flex flex-wrap items-center justify-between gap-3 rounded-medium border border-default-200 p-4'>
          <div className='flex min-w-0 flex-col gap-1'>
            <div className='flex flex-wrap items-center gap-2'>
              <span className='font-mono text-sm font-semibold'>
                {selected.reference_number}
              </span>
              <span className='text-sm font-medium'>
                {selected.receiver || '—'}
              </span>
            </div>
            <span className='text-xs text-default-500'>
              {selected.container_number || t('Sin contenedor')}
            </span>
          </div>
          <div className='text-right'>
            <p className='font-mono font-semibold'>
              {totalKilos.toLocaleString()}{' '}
              <span className='font-sans text-xs font-normal text-default-500'>
                kg
              </span>
            </p>
            <p className='text-xs text-default-500'>
              {t(weighted.length === 1 ? '{{n}} línea' : '{{n}} líneas', {
                n: weighted.length,
              })}
            </p>
          </div>
        </div>
      )}
    </NewCaseSection>
  );

  return (
    <Modal
      isOpen
      size='5xl'
      scrollBehavior='inside'
      isDismissable={!locked}
      isKeyboardDismissDisabled={locked}
      hideCloseButton={locked}
      classNames={{
        base: 'my-4 max-h-[calc(100dvh-2rem)] sm:my-4 sm:max-w-[min(1280px,calc(100vw-3rem))]',
      }}
      onClose={() => {
        if (!locked) onClose();
      }}
    >
      <ModalContent>
        <NewCaseForm
          key={formShipment ?? 'none'}
          scope={scope}
          identity={`${identity}:new:${formShipment ?? 'none'}`}
          options={ready && cargo.data ? cargo.data : null}
          shipmentId={picker.id}
          despacho={despacho}
          onSaved={onSaved}
          onCancel={onClose}
          onLockedChange={setLocked}
        />
      </ModalContent>
    </Modal>
  );
}
